import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Trophy,
  Clock,
  ArrowRight,
  Crown,
  Sparkles,
  ArrowLeft,
  Ticket,
  BadgeCheck,
} from "lucide-react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { formatEuros } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      dateStyle: "full",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export default function MyQualifications() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quals, setQuals] = useState(null);
  const [registered, setRegistered] = useState(null);

  useEffect(() => {
    if (user === null) return;
    if (user === false) {
      navigate("/login?next=/mes-qualifications");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [q, r] = await Promise.all([
          api.get("/play/me/qualifications"),
          api.get("/challenges/me/registered"),
        ]);
        if (!cancelled) {
          setQuals(q.data.items || []);
          setRegistered(r.data.items || []);
        }
      } catch (e) {
        if (!cancelled) {
          toast.error(formatApiError(e));
          setQuals([]);
          setRegistered([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, navigate]);

  const loading = quals === null || registered === null;
  // v2.43 — 3 buckets: upcoming registered défis, active qualifications,
  // and money-finales (qualifications whose destination has a cash prize).
  const upcoming = (registered || []).filter(
    (c) => c.status !== "finalized" && c.status !== "closed_manually",
  );
  const qualifiedFinales = (quals || []).filter(
    (q) =>
      Number(q.destination_prize_pool_base) > 0 && q.status !== "cancelled",
  );
  const otherQuals = (quals || []).filter(
    (q) =>
      !(Number(q.destination_prize_pool_base) > 0) && q.status !== "cancelled",
  );

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-16 sm:pb-24">
        <button
          data-testid="qualifications-back-home"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à l&apos;accueil
        </button>

        <div className="mb-10">
          <div className="inline-flex items-center gap-2 text-amber-300 text-xs uppercase tracking-[0.3em] font-mono-stat mb-3">
            <Crown className="w-3.5 h-3.5" />
            Mon parcours
          </div>
          <h1
            data-testid="qualifications-page-title"
            className="font-display font-black uppercase tracking-tighter text-3xl sm:text-5xl leading-[0.95]"
          >
            Tes défis,{" "}
            <span className="text-amber-400 neon-text-primary">tes finales</span>
          </h1>
          <p className="text-white/65 mt-4 max-w-xl text-sm sm:text-base">
            Retrouve ici les défis auxquels tu es inscrit, tes qualifications en
            cours, et les finales où ta place est déjà réservée.
          </p>
        </div>

        {loading ? (
          <div className="panel rounded-2xl p-6 text-white/60 text-sm">
            Chargement…
          </div>
        ) : (
          <>
            <Section
              testid="my-upcoming-section"
              icon={<Ticket className="w-4 h-4" />}
              title="Mes défis à venir"
              subtitle="Défis auxquels tu t'es inscrit (via code ou réservation gratuite)."
              count={upcoming.length}
              empty="Aucun défi à venir. Trouves-en un sur l'accueil !"
            >
              {upcoming.map((c) => (
                <UpcomingCard key={c.id} c={c} onOpen={() => navigate(`/challenges/${c.id}`)} />
              ))}
            </Section>

            <Section
              testid="my-finales-section"
              icon={<Trophy className="w-4 h-4" />}
              title="Mes finales"
              subtitle="Finales à cagnotte où ta place est déjà qualifiée."
              count={qualifiedFinales.length}
              empty="Tu n'es pas encore qualifié pour une finale. Participe à un quiz qualificatif !"
              accent="amber"
            >
              {qualifiedFinales.map((q) => (
                <QualificationCard
                  key={q.id}
                  q={q}
                  onOpen={() => navigate(`/challenges/${q.destination_challenge_id}`)}
                />
              ))}
            </Section>

            <Section
              testid="my-qualifications-section"
              icon={<Sparkles className="w-4 h-4" />}
              title="Mes qualifications en cours"
              subtitle="Qualifications actives dont la finale n'a pas de cagnotte en argent."
              count={otherQuals.length}
              empty="Aucune qualification supplémentaire."
              accent="cyan"
              hideWhenEmpty
            >
              {otherQuals.map((q) => (
                <QualificationCard
                  key={q.id}
                  q={q}
                  onOpen={() => navigate(`/challenges/${q.destination_challenge_id}`)}
                />
              ))}
            </Section>
          </>
        )}
      </div>
    </Layout>
  );
}

function Section({
  testid,
  icon,
  title,
  subtitle,
  count,
  empty,
  accent = "cyan",
  hideWhenEmpty = false,
  children,
}) {
  if (hideWhenEmpty && count === 0) return null;
  const accentText =
    accent === "amber" ? "text-amber-300" : "text-cyan-300";
  return (
    <section data-testid={testid} className="mb-12">
      <div className="flex items-end justify-between mb-4 gap-3">
        <div>
          <h2 className="font-display text-xl sm:text-2xl font-bold uppercase tracking-tight flex items-center gap-2">
            <span className={accentText}>{icon}</span>
            {title}
          </h2>
          {subtitle && (
            <p className="text-white/50 text-xs mt-1">{subtitle}</p>
          )}
        </div>
        <span
          className={`text-[10px] uppercase tracking-[0.3em] font-mono-stat ${accentText}/70`}
        >
          {count} élément{count > 1 ? "s" : ""}
        </span>
      </div>
      {count === 0 ? (
        <div
          data-testid={`${testid}-empty`}
          className="panel rounded-2xl px-6 py-8 text-center text-white/50 text-sm"
        >
          {empty}
        </div>
      ) : (
        <div className="grid gap-4">{children}</div>
      )}
    </section>
  );
}

function UpcomingCard({ c, onOpen }) {
  const isGolden = c.category === "golden";
  const startsMs = c.starts_at ? new Date(c.starts_at).getTime() : 0;
  const isOpen = c.status === "open" || (startsMs > 0 && Date.now() >= startsMs);
  return (
    <div
      data-testid={`upcoming-card-${c.id}`}
      onClick={onOpen}
      className={`relative rounded-2xl p-5 border cursor-pointer transition-all hover:-translate-y-1 ${
        isGolden
          ? "border-amber-400/45 bg-gradient-to-br from-amber-400/12 via-amber-400/4 to-transparent"
          : "border-cyan-400/35 bg-gradient-to-br from-cyan-400/8 via-transparent to-transparent"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.3em] text-cyan-300 font-mono-stat mb-2">
            <BadgeCheck className="w-3 h-3" />
            Tu es inscrit
          </div>
          <div
            data-testid={`upcoming-card-title-${c.id}`}
            className="font-display font-bold text-lg sm:text-xl leading-tight text-white truncate"
          >
            {c.title}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-white/60 mt-1">
            <Clock className="w-3.5 h-3.5 text-cyan-300" />
            {isOpen ? "Ouvert · Jouer maintenant" : `Ouvre le ${fmtDate(c.starts_at)}`}
          </div>
        </div>
        <ArrowRight className="w-5 h-5 text-white/40 mt-1" />
      </div>
    </div>
  );
}

function QualificationCard({ q, onOpen }) {
  const canPlay = q.destination_status === "published" && q.access_code;
  return (
    <div
      data-testid={`qualification-card-${q.id}`}
      className="relative rounded-2xl p-5 sm:p-6 border border-amber-400/40 bg-gradient-to-b from-amber-400/10 to-amber-400/0 shadow-[0_0_35px_rgba(251,191,36,0.15)]"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] text-amber-300 font-mono-stat mb-2">
            <Sparkles className="w-3 h-3" />
            {q.status === "used" ? "Qualification utilisée" : "Tu es qualifié"}
          </div>
          <div
            data-testid={`qualification-dest-title-${q.id}`}
            className="font-display font-black uppercase text-xl sm:text-2xl leading-tight text-white"
          >
            {q.destination_challenge_title}
          </div>
          <div className="text-white/60 text-xs sm:text-sm mt-1">
            Grâce à ton rang{" "}
            <span className="font-mono-stat text-amber-200">
              #{q.source_rank}
            </span>{" "}
            sur{" "}
            <span className="text-white/80">{q.source_challenge_title}</span>{" "}
            (score {q.source_score}).
          </div>
        </div>
        {q.destination_prize_pool_base > 0 && (
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-mono-stat">
              Cagnotte
            </div>
            <div className="font-mono-stat font-black text-2xl sm:text-3xl text-amber-300 neon-text-primary">
              {formatEuros(q.destination_prize_pool_base)}
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 text-xs text-white/70 mb-4">
        <Clock className="w-3.5 h-3.5 text-cyan-300" />
        Ouvre le{" "}
        <span className="font-mono-stat text-white">
          {fmtDate(q.destination_starts_at)}
        </span>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          data-testid={`qualification-open-${q.id}`}
          onClick={onOpen}
          className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-amber-400 text-black font-display font-bold uppercase tracking-wider hover:bg-amber-300 active:scale-95 transition-all"
        >
          Voir la finale
          <ArrowRight className="w-4 h-4" />
        </button>
        {canPlay && q.access_code && (
          <div
            data-testid={`qualification-access-code-${q.id}`}
            className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-black/50 border border-amber-400/40 text-amber-200 font-mono-stat"
          >
            Ton code&nbsp;: <span className="font-bold">{q.access_code}</span>
          </div>
        )}
      </div>
    </div>
  );
}
